import { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthContext } from '@/features/auth/context/AuthContext';
import { getRolePanelPath } from '@/features/auth/roleRoutes';
import { apiClient } from '@/lib/apiClient';
import { getPersianErrorMessage } from '@/lib/persianUi';
import { isValidIranianNationalId, normalizeShabaNumber } from '@/lib/iranianNationalId';
import { normalizeIranPhone, isValidIranPhone } from '@/lib/phone';
import OtpInput from './OtpInput';
import SignupFormFields from './SignupFormFields';
import SignupStepper from './SignupStepper';
import SignupTypePicker, { SIGNUP_TYPES, TYPE_CHOICES } from './SignupTypePicker';
import '../auth.css';

const SIGNUP_DRAFT_KEY = 'signup_draft';
const NAME_PATTERN = /^[a-zA-Z\u0600-\u06FF\u200c\s]+$/u;
const LATIN_STORE_NAME_PATTERN = /^[a-zA-Z0-9\s&.'-]+$/;
const EMPTY_FORM_DATA = {
  email: '',
  password: '',
  confirmPassword: '',
  first_name: '',
  last_name: '',
  mobile_phone: '',
  national_id: '',
  location_id: null,
  latitude: null,
  longitude: null,
  address: '',
  vendor_local_phone: '',
  vendor_shop_name_fa: '',
  vendor_shop_name_en: '',
  shaba_number: '',
  company_name_fa: '',
  company_name_en: '',
  registration_number: '',
  economic_code: '',
};

function loadDraft() {
  try {
    const saved = sessionStorage.getItem(SIGNUP_DRAFT_KEY);
    if (!saved) return { formData: { ...EMPTY_FORM_DATA }, signupType: '' };

    const parsed = JSON.parse(saved);
    const { signupType, ...formValues } = parsed;
    return {
      formData: {
        ...EMPTY_FORM_DATA,
        ...formValues,
        password: '',
        confirmPassword: '',
      },
      signupType: SIGNUP_TYPES.has(signupType) ? signupType : '',
    };
  } catch {
    return { formData: { ...EMPTY_FORM_DATA }, signupType: '' };
  }
}

function buildPayload(formData, signupType, refCode, refStatus) {
  const isVendor = signupType === 'vendor' || signupType === 'factory';
  const isCompany = signupType === 'company';
  const payload = {
    role: isVendor ? 'vendor' : signupType,
    first_name: formData.first_name.trim(),
    last_name: formData.last_name.trim(),
    mobile_phone: formData.mobile_phone,
    national_id: formData.national_id.trim(),
    email: formData.email.trim() || null,
    password: formData.password,
    address: formData.address || null,
    location_id: formData.location_id,
    latitude: formData.latitude,
    longitude: formData.longitude,
  };

  if (refCode && refStatus === 'valid') payload.ref_code = refCode;

  if (isVendor) {
    payload.vendor_shop_name_fa = formData.vendor_shop_name_fa.trim();
    payload.vendor_shop_name_en = formData.vendor_shop_name_en.trim();
    payload.vendor_local_phone = formData.vendor_local_phone;
    payload.shaba_number = normalizeShabaNumber(formData.shaba_number);
    payload.is_direct_to_consumer = signupType === 'factory';
    payload.producer_type = signupType === 'factory' ? 'factory' : null;
  }

  if (isCompany) {
    payload.company_name_fa = formData.company_name_fa.trim();
    payload.company_name_en = formData.company_name_en.trim() || null;
    payload.registration_number = formData.registration_number.trim();
    payload.economic_code = formData.economic_code.trim() || null;
  }

  return payload;
}

function validateSignupForm(formData, signupType) {
  const errors = {};
  const isVendor = signupType === 'vendor' || signupType === 'factory';
  const isCompany = signupType === 'company';
  const firstName = formData.first_name.trim();
  const lastName = formData.last_name.trim();

  if (firstName.length < 2 || firstName.length > 50 || !NAME_PATTERN.test(firstName)) {
    errors.first_name = 'نام باید ۲ تا ۵۰ نویسه و فقط شامل حروف باشد.';
  }
  if (lastName.length < 2 || lastName.length > 50 || !NAME_PATTERN.test(lastName)) {
    errors.last_name = 'نام خانوادگی باید ۲ تا ۵۰ نویسه و فقط شامل حروف باشد.';
  }
  if (!isValidIranPhone(formData.mobile_phone)) {
    errors.mobile_phone = 'شماره موبایل باید ۱۰ رقم و با ۹ شروع شود.';
  }
  if (!isValidIranianNationalId(formData.national_id)) {
    errors.national_id = 'کد ملی معتبر نیست.';
  }
  if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
    errors.email = 'نشانی ایمیل معتبر نیست.';
  }
  if (formData.password.length < 8) {
    errors.password = 'رمز عبور باید حداقل ۸ نویسه باشد.';
  }
  if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'رمز عبور و تکرار آن مطابقت ندارند.';
  }

  if (isVendor) {
    if (formData.vendor_shop_name_fa.trim().length < 2 || formData.vendor_shop_name_fa.trim().length > 64) {
      errors.vendor_shop_name_fa = 'نام فارسی فروشگاه باید ۲ تا ۶۴ نویسه باشد.';
    }
    const englishName = formData.vendor_shop_name_en.trim();
    if (englishName.length < 2 || englishName.length > 64 || !LATIN_STORE_NAME_PATTERN.test(englishName)) {
      errors.vendor_shop_name_en = 'نام فروشگاه باید ۲ تا ۶۴ نویسه و با حروف لاتین باشد.';
    }
    if (!/^[0-9]{10}$/.test(formData.vendor_local_phone)) {
      errors.vendor_local_phone = 'تلفن ثابت فروشگاه باید دقیقاً ۱۰ رقم باشد.';
    }
    if (!/^IR[0-9]{24}$/.test(normalizeShabaNumber(formData.shaba_number))) {
      errors.shaba_number = 'شماره شبا باید با IR و ۲۴ رقم بعد از آن باشد.';
    }
  }

  if (isCompany) {
    if (formData.company_name_fa.trim().length < 2) {
      errors.company_name_fa = 'نام شرکت الزامی است و باید حداقل ۲ نویسه داشته باشد.';
    }
    if (!/^[0-9]{11}$/.test(formData.registration_number.trim())) {
      errors.registration_number = 'شناسه ملی شرکت باید دقیقاً ۱۱ رقم باشد.';
    }
    if (formData.economic_code.trim() && !/^[0-9]+$/.test(formData.economic_code.trim())) {
      errors.economic_code = 'کد اقتصادی باید فقط شامل رقم باشد.';
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export default function Signup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const refCode = (searchParams.get('ref') || '').trim();
  const queryType = searchParams.get('type');
  const [draft] = useState(loadDraft);
  const [formData, setFormData] = useState(draft.formData);
  const [signupType, setSignupType] = useState(
    () => (SIGNUP_TYPES.has(queryType) ? queryType : draft.signupType),
  );
  const [step, setStep] = useState(
    () => (SIGNUP_TYPES.has(queryType) || draft.signupType ? 'form' : 'type-picker'),
  );
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [resendRemaining, setResendRemaining] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const turnstileRef = useRef(null);
  const errorSummaryRef = useRef(null);
  const refStatus = useRef('idle');
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

  useEffect(() => {
    if (!refCode) return undefined;
    let cancelled = false;
    apiClient.GET(`/api/v1/referral/check/${refCode}`)
      .then(({ data, error: responseError }) => {
        if (!cancelled) refStatus.current = responseError || !data ? 'invalid' : 'valid';
      })
      .catch(() => {
        if (!cancelled) refStatus.current = 'invalid';
      });
    return () => {
      cancelled = true;
    };
  }, [refCode]);

  useEffect(() => {
    const draftSafe = { ...formData };
    delete draftSafe.password;
    delete draftSafe.confirmPassword;
    try {
      sessionStorage.setItem(SIGNUP_DRAFT_KEY, JSON.stringify({ ...draftSafe, signupType }));
    } catch {
      // Session storage is optional; the current form state remains available.
    }
  }, [formData, signupType]);

  useEffect(() => {
    if (step !== 'otp' || !resendDisabled) return undefined;
    const timer = window.setTimeout(() => {
      setResendRemaining((remaining) => {
        const next = Math.max(0, remaining - 1);
        if (next === 0) setResendDisabled(false);
        return next;
      });
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [step, resendDisabled, resendRemaining]);

  useEffect(() => {
    if (step !== 'form' || !error) return;
    errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    errorSummaryRef.current?.focus();
  }, [error, step]);

  const clearDraft = () => {
    try {
      sessionStorage.removeItem(SIGNUP_DRAFT_KEY);
    } catch {
      // Session storage is optional.
    }
  };

  const resetTurnstile = () => {
    turnstileRef.current?.reset();
    setTurnstileToken('');
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleDigitFieldChange = (event, maxLength) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value.replace(/\D/g, '').slice(0, maxLength),
    }));
  };

  const handlePhoneChange = (event) => {
    setFormData((previous) => ({
      ...previous,
      mobile_phone: normalizeIranPhone(event.target.value),
    }));
  };

  const receiveCooldown = (response) => {
    const cooldown = Number(response?.cooldown_seconds);
    if (!Number.isFinite(cooldown) || cooldown < 0) {
      throw new Error('Missing OTP cooldown from server response');
    }
    setResendRemaining(cooldown);
    setResendDisabled(cooldown > 0);
  };

  const requestSignupOtp = async () => {
    if (turnstileSiteKey && !turnstileToken) {
      throw new Error('captcha_required');
    }
    const result = await apiClient.POST('/api/v1/auth/signup/request-otp', {
      body: buildPayload(formData, signupType, refCode, refStatus.current),
      headers: turnstileSiteKey ? { 'X-Turnstile-Token': turnstileToken } : {},
    });
    if (result.error) throw result.error;
    receiveCooldown(result.data);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setOtpError('');
    const validation = validateSignupForm(formData, signupType);
    if (!validation.valid) {
      setFieldErrors(validation.errors);
      setError('لطفاً خطاهای مشخص‌شده را اصلاح کنید.');
      window.setTimeout(() => {
        errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorSummaryRef.current?.focus();
        const firstField = Object.keys(validation.errors)[0];
        document.querySelector(`[name="${firstField}"]`)?.focus({ preventScroll: true });
      }, 0);
      return;
    }

    setFieldErrors({});
    setLoading(true);
    try {
      await requestSignupOtp();
      setStep('otp');
      setOtpCode('');
    } catch (requestError) {
      setError(getPersianErrorMessage(requestError, 'درخواست کد تأیید ناموفق بود.'));
    } finally {
      resetTurnstile();
      setLoading(false);
    }
  };

  const handleOtpResend = async () => {
    if (resendDisabled) return;
    setOtpError('');
    if (turnstileSiteKey && !turnstileToken) {
      setOtpError('لطفاً تأیید امنیت را تکمیل کنید.');
      return;
    }

    try {
      const result = await apiClient.POST('/api/v1/auth/register/request-otp', {
        body: { mobile_phone: formData.mobile_phone },
        headers: turnstileSiteKey ? { 'X-Turnstile-Token': turnstileToken } : {},
      });
      if (result.error) throw result.error;
      receiveCooldown(result.data);
    } catch (requestError) {
      setOtpError(getPersianErrorMessage(requestError, 'ارسال مجدد کد ناموفق بود.'));
    } finally {
      resetTurnstile();
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setOtpError('');
    if (!/^[0-9]{5}$/.test(otpCode)) {
      setOtpError('کد تأیید باید ۵ رقم باشد.');
      setOtpCode('');
      return;
    }

    setOtpLoading(true);
    let verifiedToken;
    try {
      const verifyResponse = await apiClient.POST('/api/v1/auth/register/verify-otp', {
        body: { mobile_phone: formData.mobile_phone, code: otpCode },
      });
      if (verifyResponse.error || !verifyResponse.data?.verified_token) {
        throw verifyResponse.error || new Error('Invalid or expired code');
      }
      verifiedToken = verifyResponse.data.verified_token;
    } catch (verifyError) {
      setOtpError(getPersianErrorMessage(verifyError, 'کد واردشده نامعتبر است یا منقضی شده است.'));
      setOtpCode('');
      setOtpLoading(false);
      return;
    }

    try {
      const finalResponse = await apiClient.POST('/api/v1/auth/signup', {
        body: buildPayload(formData, signupType, refCode, refStatus.current),
        headers: { 'X-OTP-Verified-Token': verifiedToken },
      });
      if (finalResponse.error || !finalResponse.data) {
        throw finalResponse.error || new Error('Signup failed');
      }

      clearDraft();
      setOtpCode('');
      setError('');
      if (signupType === 'vendor' || signupType === 'factory') {
        navigate('/login', {
          replace: true,
          state: { message: 'ثبت‌نام انجام شد. پس از تأیید مدارک توسط مدیر می‌توانید وارد شوید.' },
        });
        return;
      }

      try {
        const loginResult = await login(formData.mobile_phone, formData.password);
        if (loginResult.requiresRoleSelection) {
          navigate('/select-role', { replace: true });
          return;
        }
        navigate(getRolePanelPath(signupType) || '/', { replace: true });
      } catch (loginError) {
        console.error('Post-signup auto-login failed:', loginError);
        navigate('/login', {
          replace: true,
          state: { message: 'ثبت‌نام با موفقیت انجام شد. لطفاً وارد حساب کاربری خود شوید.' },
        });
      }
    } catch (signupError) {
      setStep('form');
      setOtpCode('');
      setOtpError('');
      setError(getPersianErrorMessage(signupError, 'ثبت‌نام انجام نشد. لطفاً دوباره تلاش کنید.'));
    } finally {
      setOtpLoading(false);
    }
  };

  const selectedType = TYPE_CHOICES.find((choice) => choice.type === signupType);
  const stepIndex = step === 'type-picker' ? 0 : step === 'form' ? 1 : 2;
  const errorSummary = error || (Object.keys(fieldErrors).length ? 'لطفاً خطاهای مشخص‌شده را اصلاح کنید.' : '');

  return (
    <main className="signup-page">
      <section className={`signup-container ${step === 'form' ? 'signup-container-wide' : ''} ${step === 'otp' ? 'signup-container-compact' : ''}`}>
        <header className="signup-header-block">
          <div className="signup-brand-mark" aria-hidden="true">
            <i className="fas fa-user-plus" />
          </div>
          <h1>ثبت‌نام</h1>
          <p>به خانوادهٔ ایران چارسوق بپیوندید</p>
        </header>

        <SignupStepper currentStep={stepIndex} />

        {step === 'type-picker' && (
          <SignupTypePicker
            choices={TYPE_CHOICES}
            onSelect={(type) => {
              setSignupType(type);
              setStep('form');
              setError('');
              setFieldErrors({});
            }}
          />
        )}

        {step === 'form' && (
          <div className="signup-step-panel">
            <SignupFormFields
              signupType={signupType}
              selectedType={selectedType}
              formData={formData}
              fieldErrors={fieldErrors}
              errorSummary={errorSummary}
              errorSummaryRef={errorSummaryRef}
              showPassword={showPassword}
              showConfirmPassword={showConfirmPassword}
              loading={loading}
              turnstileSiteKey={turnstileSiteKey}
              turnstileRef={turnstileRef}
              onSubmit={handleFormSubmit}
              onSelectType={() => {
                setStep('type-picker');
                setError('');
                setFieldErrors({});
              }}
              onChange={handleFieldChange}
              onDigitChange={handleDigitFieldChange}
              onPhoneChange={handlePhoneChange}
              onLocationChange={(patch) => setFormData((previous) => ({ ...previous, ...patch }))}
              onTogglePassword={() => setShowPassword((visible) => !visible)}
              onToggleConfirmPassword={() => setShowConfirmPassword((visible) => !visible)}
              onTurnstileToken={setTurnstileToken}
              turnstileToken={turnstileToken}
            />
          </div>
        )}

        {step === 'otp' && (
          <div className="signup-step-panel">
            <form className="signup-otp-form" onSubmit={handleVerifyOtp} noValidate>
              <p className="signup-otp-intro">
                کد تأیید به شمارهٔ <bdi className="signup-phone-display">{formData.mobile_phone}</bdi> ارسال شد.
              </p>
              <button
                type="button"
                className="signup-edit-link"
                onClick={() => {
                  setStep('form');
                  setOtpCode('');
                  setOtpError('');
                }}
              >
                <i className="fas fa-pen" aria-hidden="true" />
                ویرایش اطلاعات
              </button>

              <OtpInput value={otpCode} onChange={setOtpCode} />
              {otpError && <p className="signup-otp-error" role="alert">{otpError}</p>}

              {turnstileSiteKey && (
                <div className="signup-turnstile">
                  <TurnstileWidget
                    ref={turnstileRef}
                    siteKey={turnstileSiteKey}
                    action="register_otp"
                    onToken={setTurnstileToken}
                    onExpire={() => setTurnstileToken('')}
                    onError={() => setTurnstileToken('')}
                  />
                </div>
              )}

              <button
                type="button"
                className="btn btn-secondary signup-resend-button"
                onClick={handleOtpResend}
                disabled={resendDisabled}
              >
                {resendDisabled
                  ? `ارسال مجدد کد تا ${String(Math.floor(resendRemaining / 60)).padStart(2, '0')}:${String(resendRemaining % 60).padStart(2, '0')}`
                  : 'ارسال دوبارهٔ کد'}
              </button>
              <button type="submit" className="btn btn-primary signup-submit-button" disabled={otpLoading}>
                {otpLoading ? <><span className="signup-spinner" aria-hidden="true" /> در حال بررسی...</> : 'تأیید و ثبت‌نام'}
              </button>
            </form>
          </div>
        )}

        <p className="login-link">
          حساب کاربری دارید؟ <Link to="/login">ورود</Link>
        </p>
      </section>
    </main>
  );
}
